import { useScrollReveal } from '../../../hooks/useScrollReveal';

const STEPS = [
  {
    number: '01',
    icon: 'ri-phone-line',
    title: 'Solicita tu Consulta',
    desc: 'Contáctanos por teléfono, email o a través de nuestro formulario web. La primera consulta es completamente gratuita y sin compromiso. Te explicamos cómo puede ayudarte la mediación en tu caso concreto.',
    detail: 'Tiempo estimado: 30 minutos',
  },
  {
    number: '02',
    icon: 'ri-user-voice-line',
    title: 'Primera Sesión con el Mediador',
    desc: 'El mediador se reúne con ambas partes para conocer el conflicto, establecer las reglas del proceso y definir los objetivos del acuerdo. Se garantiza un ambiente seguro y neutral.',
    detail: 'Duración: 1-2 horas',
  },
  {
    number: '03',
    icon: 'ri-discuss-line',
    title: 'Sesiones de Negociación',
    desc: 'A través de sesiones estructuradas, el mediador facilita el diálogo y ayuda a las partes a explorar soluciones creativas que satisfagan los intereses de todos. Se pueden realizar de forma presencial u online.',
    detail: 'Entre 2 y 6 sesiones',
  },
  {
    number: '04',
    icon: 'ri-file-text-line',
    title: 'Acuerdo Final',
    desc: 'Una vez alcanzado el acuerdo, se redacta el documento de acuerdo de mediación. Si es necesario, puede elevarse a escritura pública para darle plena eficacia legal.',
    detail: 'Documento legalmente vinculante',
  },
];

export default function HowItWorks() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>();

  return (
    <section ref={ref} id="como-funciona" className="py-28 bg-[#1B3A5C] relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#3D9E8C]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#3D9E8C]/8 rounded-full translate-y-1/2 -translate-x-1/2" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div
          className={`text-center mb-16 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: isVisible ? '100ms' : '0ms' }}
        >
          <span className="text-[#3D9E8C] text-sm font-semibold tracking-widest uppercase">El Proceso</span>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            ¿Cómo funciona<br />la mediación?
          </h2>
          <p className="mt-4 text-white/60 text-[15px] max-w-xl mx-auto leading-relaxed">
            Un proceso estructurado, flexible y confidencial que puede resolverse en semanas, no en años.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {STEPS.map((step, idx) => (
            <div
              key={step.number}
              className={`relative group h-full transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: isVisible ? `${250 + idx * 120}ms` : '0ms' }}
            >
              <div className="h-full flex flex-col relative z-10 bg-white/8 border border-white/10 rounded-2xl p-6 hover:bg-white/12 transition-all duration-300">
                <div className="flex items-center justify-between mb-5">
                  <span className="text-5xl font-bold text-white/10" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {step.number}
                  </span>
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#3D9E8C]/20">
                    <i className={`${step.icon} text-[#3D9E8C] text-xl`} />
                  </div>
                </div>
                <h3 className="font-bold text-white text-base mb-3">{step.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-5 flex-1">{step.desc}</p>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-[#3D9E8C] text-sm" />
                  <span className="text-[#3D9E8C] text-xs font-medium">{step.detail}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className={`mt-16 text-center transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: isVisible ? '750ms' : '0ms' }}
        >
          <p className="text-white/60 text-sm mb-5">¿Listo para dar el primer paso?</p>
          <button
            onClick={() => {
              const el = document.getElementById('contacto');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-8 py-4 bg-[#3D9E8C] text-white text-base font-semibold rounded-full hover:bg-[#2d8a78] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Solicitar Primera Consulta Gratuita
          </button>
        </div>
      </div>
    </section>
  );
}
